import { Module } from '@by-association-only/theme-module';
import { getElement, getElements } from '@by-association-only/theme-utils';
const SECTION_ID = 'product-list';
const HEADER_HEIGHT_CUSTOM_PROPERTY = '--header__height'; // eslint-disable-next-line import/no-default-export

export default class _class extends Module {
  constructor(...args) {
    super(...args);
    this.needsToJumpToCard = Boolean(window.location.hash);
    this.lastKnownY = window.scrollY;
    this.scrollDirection = 'down';
  }

  get firstPage() {
    return Number(this.data.get('firstPage'));
  }

  set firstPage(page) {
    this.data.set('firstPage', page);

    if (page === 1) {
      this.observerTop.disconnect();
    }
  }

  get currentPage() {
    return Number(this.data.get('currentPage'));
  }

  set currentPage(page) {
    this.data.set('currentPage', page);

    if (page >= this.totalPages) {
      this.observer.disconnect();
    }
  }

  get previousPage() {
    return this.firstPage - 1;
  }

  get nextPage() {
    return this.currentPage + 1;
  }

  get totalPages() {
    return Number(this.data.get('totalPages'));
  }

  set totalPages(total) {
    this.data.set('totalPages', Number(total));
  }

  get isLoading() {
    return this.data.get('isLoading') === 'true';
  }

  set isLoading(isLoading) {
    this.data.set('isLoading', isLoading);
  }

  setupObservers() {
    this.observer = new IntersectionObserver(this.onTriggerEnter);
    this.observerTop = new IntersectionObserver(this.onTriggerEnter, {
      threshold: [0, 1]
    });

    if (this.currentPage < this.totalPages) {
      this.observer.observe(this.observableEl);
    }

    if (this.firstPage > 1) {
      this.observerTop.observe(this.observableTopEl);
    }
  }

  initialize() {
    super.initialize();

    if (window.location.hash) {
      // Scroll to correct position on 'back'. Need to wait for complete due to Chrome returning to previous position
      document.addEventListener('readystatechange', () => {
        if (document.readyState !== 'complete') {
          return;
        }

        window.setTimeout(() => {
          const previousItem = getElement(`[data-id="${window.location.hash.replace('#', '')}"]`);
          this.scrollToElement(previousItem);
          this.setupObservers();
          window.history.pushState({}, '', window.location.href.split('#')[0]);
        }, 250);
      });
    } else {
      this.needsToJumpToCard = false;
      this.setupObservers();
    }
  }

  setupListeners() {
    super.setupListeners();
    this.listeners.add(window, 'scroll', this.onScroll);
    this.listeners.add(window, 'popstate', this.handlePopstate);
    this.listeners.add(document.body, 'filters:update', this.handleFiltersUpdate);
    this.listeners.add(document.body, 'sort:update', this.handleSortUpdate);
    this.itemEls.forEach(itemEl => {
      this.listeners.add(itemEl, 'click', this.updateHash);
    });
  }

  handleFiltersUpdate() {
    this.renderPage(1, 'replace');
  }

  handleSortUpdate() {
    this.renderPage(1, 'replace');
  }

  handlePopstate() {
    if (!this.needsToJumpToCard) {
      this.renderPage(1, 'replace');
    }
  }
  /** @param {IntersectionObserverEntry[]} entries */


  onTriggerEnter(entries) {
    const intersectionElement = entries.find(entry => entry.isIntersecting);

    if (!intersectionElement) {
      return;
    }

    if (intersectionElement.target === this.observableEl) {
      this.renderPage(this.nextPage);
    } else if (intersectionElement.target === this.observableTopEl && this.scrollDirection === 'up') {
      this.renderPage(this.previousPage, 'prepend');
    }
  }

  emptyList() {
    this.listEl.innerHTML = '';
  }
  /** @param {number} [page] */


  async fetchPage(page = 1) {
    this.isLoading = true;
    const search = new URLSearchParams(window.location.search);
    search.set('section_id', SECTION_ID);
    search.set('page', page);
    const response = await fetch(`${window.location.pathname}?${search}`);
    return response.text();
  }
  /**
   * @param {number} page
   * @param {'append'|'prepend'|'replace'} method
   */


  async renderPage(page, method = 'append') {
    if (this.isLoading) {
      return;
    }

    const domString = await this.fetchPage(page);
    const html = new DOMParser().parseFromString(domString, 'text/html');
    const section = getElement(`[data-section-id="${SECTION_ID}"]`, {
      context: html
    });
    const listItems = getElements(`[data-el="${SECTION_ID}.item"]`, {
      context: html
    });

    if (method === 'replace') {
      this.emptyList();
      this.totalPages = section.dataset.productListTotalPages;
    }

    if (method === 'prepend') {
      listItems.reverse().forEach(listItem => this.listEl.prepend(listItem));
      this.firstPage = page;
      this.scrollToElement(getElement(`[data-page="${page + 1}"]`));
    } else {
      listItems.forEach(listItem => this.listEl.appendChild(listItem));
      this.currentPage = page;
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('page', page);
      window.history.pushState({}, '', `?${searchParams.toString()}`);
    }

    listItems.forEach(itemEl => {
      this.listeners.add(itemEl, 'click', this.updateHash);
    });
    this.isLoading = false;
  }

  onScroll() {
    this.scrollDirection = this.lastKnownY > window.scrollY ? 'up' : 'down';
    this.lastKnownY = window.scrollY;
  }
  /** @param {HTMLElement} el */


  scrollToElement(el) {
    if (!el) {
      return;
    }
    /**
     * @note For some reason el.scrollIntoView() wasn't working here,
     * so we're having to calculate the top of the element and scroll the
     * window to that position manually
     */


    const headerHeight = parseInt(window.getComputedStyle(this.el).getPropertyValue(HEADER_HEIGHT_CUSTOM_PROPERTY), 10);
    const boundingRect = el.getBoundingClientRect();
    const distanceFromTopOfPage = window.pageYOffset + boundingRect.top - headerHeight;
    window.scrollTo(0, distanceFromTopOfPage);
    this.needsToJumpToCard = false;
  }
  /** @param {Event} e */


  updateHash(e) {
    e.preventDefault();
    const listItem = e.target.closest('.prd-List_Item');
    const id = listItem.dataset.id;
    const page = listItem.dataset.page;
    const url = new URL(window.location);
    url.searchParams.set('page', page);
    url.hash = id;
    window.history.pushState({}, '', `${url.search}${url.hash}`);
    window.location = e.target.getAttribute('href');
  }

}
_class.methods = ['handleFiltersUpdate', 'handleSortUpdate', 'handlePopstate', 'onTriggerEnter', 'onScroll', 'updateHash'];
_class.targets = ['item', 'list', 'observable', 'observableTop'];